/**
 * API Configuration
 * Centralized configuration for all API endpoints
 * Uses environment variables for different environments
 * 
 * In development: Uses proxy from package.json (no localhost needed)
 * In production: Uses REACT_APP_API_BASE_URL environment variable
 */

// Get API base URL from environment variable and/or current host
// In production, this should resolve to your backend URL (e.g., https://api.parrotseo.com)
// In development, proxy in package.json handles routing, so we use relative URLs
const getApiBaseUrl = () => {
  // 1) Explicit env override (used in most production/staging setups)
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL;
  }

  // 2) Host-based fallback for production without env override
  //    Ensure app.parrotseo.com always talks to api.parrotseo.com
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;

    // Primary production app
    if (host === 'app.parrotseo.com') {
      return 'https://api.parrotseo.com';
    }
  }
  
  // 3) Development: use empty string for relative URLs
  //    The proxy in package.json will forward /api/* to http://localhost:5001/api/*
  //    This avoids CORS issues and localhost permission prompts
  return '';
};

export const API_BASE_URL = getApiBaseUrl();

/**
 * Get full API URL for an endpoint
 * @param {string} endpoint - API endpoint (e.g., 'analyze', 'auth/login')
 * @returns {string} Full API URL
 */
export const getApiUrl = (endpoint) => {
  const baseUrl = API_BASE_URL;
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
  
  if (baseUrl) {
    // Production: Use full URL
    const cleanBaseUrl = baseUrl.replace(/\/$/, '');
    return `${cleanBaseUrl}/api/${cleanEndpoint}`;
  }
  
  // Development: Use relative URL (proxy handles routing)
  return `/api/${cleanEndpoint}`;
};

/**
 * Default axios configuration
 */
export const axiosConfig = {
  timeout: 60000, // 60 seconds
  headers: {
    'Content-Type': 'application/json',
  },
};

const apiConfig = {
  API_BASE_URL,
  getApiUrl,
  axiosConfig,
};

export default apiConfig;
